import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Loader2, Send, ChevronRight, ShieldCheck, FlaskConical } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { OrgSales360 } from "@/components/common/OrgSales360";
import { formatDate } from "@/lib/nav";

export default function SalesClaims() {
  const { t } = useTranslation();
  const [claims, setClaims] = useState(null);
  const [qaOrg, setQaOrg] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setClaims(null);
    api.get("/sales/claims").then((r) => setClaims(r.data)).catch(() => setClaims([]));
    api.get("/sales/qa-org").then((r) => setQaOrg(r.data.qa_org)).catch(() => setQaOrg(null));
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="sales-claims-page">
      <PageHeader title={t("claims.pageTitle")} subtitle={t("claims.pageSubtitle")} />

      {qaOrg && (
        <div data-testid="claim-qa-card" className="mb-5 rounded-xl border border-status-warning-border bg-status-warning-bg/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-status-warning-bg text-status-warning"><FlaskConical className="h-5 w-5" /></div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-sm font-semibold text-fundio-ink">{qaOrg.org_name}</span>
                  <span className="rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 text-[10px] font-semibold text-status-warning">{t("claims.qaBadge")}</span>
                </div>
                <p className="mt-0.5 max-w-xl text-xs text-fundio-muted">{t("claims.qaCardDesc", { tenant: qaOrg.canonical_tenant_id })}</p>
              </div>
            </div>
            <button data-testid="claim-qa-open-btn" onClick={() => setSelected(qaOrg.organization_id)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-fundio-gold px-4 py-2 text-sm font-semibold text-fundio-dark transition-colors hover:bg-fundio-gold-bright">
              {t("claims.qaOpen")} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div data-testid="claims-phase-note" className="mb-5 flex items-center gap-2 rounded-lg border border-status-info-border bg-status-info-bg/50 px-4 py-2 text-xs text-status-info">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <span>{t("claims.phaseNote")}</span>
      </div>

      <Panel testid="sales-claims-panel" noPad title={t("claims.legacyTitle")}>
        {claims === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : claims.length === 0 ? (
          <div className="p-4"><EmptyState icon={Send} title={t("claims.legacyEmptyTitle")} description={t("claims.legacyEmptyDesc")} testid="claims-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                  <th className="px-4 py-3">{t("claims.hClaimId")}</th><th className="px-4 py-3">{t("claims.hOrg")}</th><th className="px-4 py-3">{t("claims.hStatus")}</th>
                  <th className="px-4 py-3">{t("claims.hCreated")}</th><th className="px-4 py-3">{t("claims.hValidUntil")}</th><th className="px-4 py-3">{t("claims.hPreparedBy")}</th><th className="px-4 py-3 text-right">{t("common.action")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fundio-border">
                {claims.map((c) => (
                  <tr key={c.claim_id} data-testid={`claim-row-${c.claim_id}`} onClick={() => setSelected(c.organization_id)} className="cursor-pointer bg-white transition-colors hover:bg-fundio-subtle">
                    <td className="px-4 py-3 font-mono text-[11px] text-fundio-faint">{c.claim_id}</td>
                    <td className="px-4 py-3 font-medium text-fundio-ink">{c.org_name}</td>
                    <td className="px-4 py-3"><span className="inline-flex rounded-md border border-status-info-border bg-status-info-bg px-2 py-0.5 text-xs font-semibold text-status-info">{c.status}</span></td>
                    <td className="px-4 py-3 text-xs text-fundio-muted">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-fundio-muted">{formatDate(c.expires_at)}</td>
                    <td className="px-4 py-3 text-xs text-fundio-muted">{c.prepared_by}</td>
                    <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 text-sm font-semibold text-fundio-ink">{t("common.open")} <ChevronRight className="h-4 w-4" /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <OrgSales360 organizationId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}
